/**
 * Cuanta plata hay: los dos totales, el consolidado y el desglose por cuenta.
 *
 * LA REGLA QUE ORDENA TODO ESTE ARCHIVO
 * -------------------------------------
 * Los pesos y los dolares no se suman. Hay un total en pesos y hay un total en
 * dolares, y son dos numeros distintos que no se tocan.
 *
 * El consolidado existe igual, porque la pregunta "cuanta plata hay en total"
 * es legitima, pero es una CONVERSION, no una suma: solo significa algo si se
 * sabe a que cotizacion se hizo. Va en cuerpo chico, al costado y con la
 * advertencia entera detras de una `i`, para que no se lea como un tercer
 * total.
 *
 * POR QUE UNA FRANJA Y NO SIETE TARJETAS
 * --------------------------------------
 * Antes esto eran cuatro tarjetas de cuenta mas dos de total mas una de
 * consolidado, todas del mismo tamaño: una cuenta con $ 2.000 pesaba lo mismo
 * que el total de $ 19 M. Las cuentas no son siete cosas distintas, son el
 * desglose de esos dos totales, asi que van adentro de la misma tarjeta y
 * debajo.
 *
 * Las filas de cuenta no llevan la barra lateral de moneda: como en una tabla,
 * el chip y el color del importe alcanzan. La barra es el tercer refuerzo para
 * una tarjeta suelta, no para una lista donde las filas se leen una abajo de la
 * otra.
 *
 * Y llevan DOS cosas, no cinco: el nombre con su chip de moneda, y el saldo.
 * Con cinco, cada fila arrancaba sus numeros donde terminaba su nombre y no
 * habia ninguna columna que se pudiera seguir con el ojo hacia abajo.
 */
import { Link } from "@tanstack/react-router";
import { ChevronRight, Info, Landmark, Smartphone, Wallet } from "lucide-react";

import { ChipMoneda, Importe } from "@/components/money";
import { Card } from "@/components/ui/card";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { aNumero, type Caja, type Cheque, type TotalMoneda, type TotalesCaja } from "@/lib/tipos";

import { ETIQUETA_TIPO_CUENTA, textoCotizacion } from "./logica";

const ICONO_TIPO: Record<string, typeof Wallet> = {
  CAJA: Wallet,
  BANCO: Landmark,
  BILLETERA: Smartphone,
};

/** El neto de tarjeta todavia no acreditado, por moneda. */
export interface PorAcreditar {
  ars: TotalMoneda;
  usd: TotalMoneda;
}

export interface FranjaSaldosProps {
  cajas: ReadonlyArray<Caja>;
  totales: TotalesCaja | null;
  /** `null` mientras carga o cuando no hay nada esperando acreditacion. */
  porAcreditar?: PorAcreditar | null;
  /**
   * Los cheques cobrados que siguen en el cajon (ver `chequesEnCartera`). Ya
   * estan sumados en el saldo de la cuenta donde se recibieron.
   */
  enCartera?: PorAcreditar | null;
}

/**
 * Lo que de los saldos de arriba es papel y no plata en el banco.
 *
 * Un cheque cobrado suma al saldo de la cuenta el dia del cobro (0068 lo
 * explica: depositarlo despues solo lo mueve). Sin decirlo, el Banco mostraba
 * $ 35.000 con el cheque todavia en el cajon, y el saldo no cerraba contra el
 * extracto. Solo cuentan los que vinieron de un cobro (`movimiento_id`): un
 * cheque cargado a mano no sumo nada, y entra recien al depositarlo.
 */
export function chequesEnCartera(cheques: ReadonlyArray<Cheque>): PorAcreditar {
  const total = { ars: { importe: 0, cantidad: 0 }, usd: { importe: 0, cantidad: 0 } };
  for (const ch of cheques) {
    if (ch.estado !== "EN_CARTERA" || ch.movimiento_id == null) continue;
    const t = ch.moneda === "USD" ? total.usd : total.ars;
    t.importe = Math.round((t.importe + aNumero(ch.importe)) * 100) / 100;
    t.cantidad += 1;
  }
  return total;
}

export function FranjaSaldos({
  cajas,
  totales,
  porAcreditar,
  enCartera,
}: FranjaSaldosProps): JSX.Element {
  const cotizacion = aNumero(totales?.cotizacion);
  const pendientes = monedasConPlata(porAcreditar);
  const cheques = monedasConPlata(enCartera);

  return (
    <Card as="section" padding="none" className="divide-y divide-border">
      <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
        <TotalMonedaBloque etiqueta="En pesos" monto={aNumero(totales?.total_ars)} moneda="ARS" />
        <TotalMonedaBloque etiqueta="En dólares" monto={aNumero(totales?.total_usd)} moneda="USD" />
        <Consolidado totales={totales} cotizacion={cotizacion} />
      </div>

      {cajas.length === 0 ? (
        <div className="p-4">
          <EstadoVacio icono={Wallet} titulo="No hay cuentas financieras cargadas todavía." />
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {cajas.map((c) => (
            <FilaCuenta key={c.id} caja={c} />
          ))}
        </ul>
      )}

      {/*
        Lleva a la solapa Tarjetas: sin la solapa en la URL, Ganancias abria en
        «Por día» y lo que se habia tocado no estaba a la vista.
      */}
      {pendientes.length > 0 ? (
        <Link
          to="/ganancias"
          search={{ vista: "tarjetas" }}
          className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium">Tarjetas por acreditar</p>
            <p className="text-xs text-muted-foreground">
              {/* Decia «No suma a los saldos de arriba», y SI suma: el cobro entra a
                  la cuenta el dia de la venta (v_saldos_cajas), y esto es lo que de
                  ese saldo todavia no acredito la tarjeta. */}
              Ya está sumado en el saldo de su cuenta; falta que se acredite.
            </p>
          </div>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {pendientes.map(({ moneda, total }) => (
              <span key={moneda} className="inline-flex items-center gap-2">
                <Importe monto={aNumero(total.importe)} moneda={moneda} tamano="sm" />
                <span className="text-xs tabular-nums text-muted-foreground">
                  {total.cantidad} {total.cantidad === 1 ? "operación" : "operaciones"}
                </span>
              </span>
            ))}
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </span>
        </Link>
      ) : null}

      {cheques.length > 0 ? (
        <Link
          to="/cheques"
          className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium">Cheques en cartera</p>
            <p className="text-xs text-muted-foreground">
              Ya está sumado en el saldo de su cuenta; falta depositarlo.
            </p>
          </div>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {cheques.map(({ moneda, total }) => (
              <span key={moneda} className="inline-flex items-center gap-2">
                <Importe monto={aNumero(total.importe)} moneda={moneda} tamano="sm" />
                <span className="text-xs tabular-nums text-muted-foreground">
                  {total.cantidad} {total.cantidad === 1 ? "cheque" : "cheques"}
                </span>
              </span>
            ))}
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </span>
        </Link>
      ) : null}
    </Card>
  );
}

/**
 * El consolidado: un solo numero, y la letra chica en un tooltip.
 *
 * Antes eran dos importes con chip, un "o" en el medio y tres renglones de
 * explicacion: el bloque mas ruidoso de la franja siendo el menos importante de
 * los tres. La advertencia sigue estando entera —es la que evita que alguien lo
 * lea como un total duro— pero detras de la `i`, que es donde va lo que se lee
 * una vez y despues ya se sabe.
 */
function Consolidado({
  totales,
  cotizacion,
}: {
  totales: TotalesCaja | null;
  cotizacion: number;
}): JSX.Element {
  const hayCotizacion = cotizacion > 0;

  return (
    /*
      Alineado a la derecha desde `md`: es la ultima columna de la franja y el
      borde derecho de la tarjeta es el mismo contra el que alinean los saldos
      de las cuentas, justo abajo. Apilado en mobile vuelve a la izquierda, que
      es donde arrancan los otros dos totales.
    */
    <div className="flex flex-col gap-1 md:items-end md:text-right">
      <span className="flex items-center gap-1">
        <TituloSeccion nivel="etiqueta" as="span">
          Todo junto
        </TituloSeccion>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Boton crudo: disparador de tooltip al lado de una etiqueta de
                12px, donde IconButton (32px) pesaria mas que el titulo. */}
            <button
              type="button"
              aria-label="Qué significa este número"
              className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info className="size-3.5" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {hayCotizacion ? (
              <>
                Los pesos y los dólares no se suman: esto son los dos totales expresados en una sola
                moneda, a US$ 1 = $ {textoCotizacion(cotizacion)}. Es una conversión, no una suma:
                si mañana cambia el dólar, cambia este número.
              </>
            ) : (
              "Sin cotización cargada no se puede convertir una moneda a la otra."
            )}
          </TooltipContent>
        </Tooltip>
      </span>

      {hayCotizacion ? (
        <>
          <Importe
            monto={aNumero(totales?.total_consolidado_ars)}
            moneda="ARS"
            codigo={false}
            className="font-medium"
          />
          <p className="text-xs text-muted-foreground">
            o{" "}
            <Importe
              monto={aNumero(totales?.total_consolidado_usd)}
              moneda="USD"
              tamano="sm"
              codigo={false}
            />
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Sin cotización</p>
      )}
    </div>
  );
}

/** Uno de los dos numeros que mandan: grande, con su etiqueta arriba. */
function TotalMonedaBloque({
  etiqueta,
  monto,
  moneda,
}: {
  etiqueta: string;
  monto: number;
  moneda: "ARS" | "USD";
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <TituloSeccion nivel="etiqueta">{etiqueta}</TituloSeccion>
      <Importe monto={monto} moneda={moneda} tamano="xl" codigo={false} className="font-semibold" />
    </div>
  );
}

/**
 * Una cuenta: cual es y cuanto tiene. Dos cosas, no cinco.
 *
 * EL CHIP DE MONEDA VA PEGADO AL NOMBRE
 * -------------------------------------
 * Dos cuentas se pueden llamar igual ("Efectivo" en pesos y "Efectivo" en
 * dolares) y de hecho se llaman igual. Con el chip al final del renglon, lo
 * unico que las distingue queda a media pantalla del nombre que hay que
 * distinguir. Va al lado, y el simbolo del saldo ($ contra US$) lo repite del
 * otro lado.
 *
 * POR QUE NO ESTAN MAS LAS ENTRADAS Y SALIDAS
 * -------------------------------------------
 * `v_saldos_cajas` las calcula sobre TODA la historia de la cuenta, sin filtro
 * de fecha. Puestas arriba de una lista filtrada por mes se leen como el
 * movimiento del mes, que es justo lo que no son. Cuanto se movio en el periodo
 * lo contesta el pie de la tabla, que si esta filtrado.
 */
function FilaCuenta({ caja }: { caja: Caja }): JSX.Element {
  const Icono = ICONO_TIPO[caja.tipo] ?? Wallet;
  return (
    <li className="flex items-center justify-between gap-4 px-4 py-2">
      <span className="flex min-w-0 items-center gap-2">
        <Icono className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="truncate text-sm font-medium">{caja.nombre}</span>
        <ChipMoneda moneda={caja.moneda} />
        <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
          {ETIQUETA_TIPO_CUENTA[caja.tipo] ?? caja.tipo}
        </span>
      </span>

      {/* Sin chip: ya esta al lado del nombre, y el simbolo lo dice igual. */}
      <Importe
        monto={aNumero(caja.saldo)}
        moneda={caja.moneda}
        codigo={false}
        className="font-medium"
      />
    </li>
  );
}

/** Solo las monedas que tienen algo esperando: un "US$ 0" no dice nada. */
function monedasConPlata(
  porAcreditar: PorAcreditar | null | undefined,
): Array<{ moneda: "ARS" | "USD"; total: TotalMoneda }> {
  if (!porAcreditar) return [];
  const filas: Array<{ moneda: "ARS" | "USD"; total: TotalMoneda }> = [];
  if (porAcreditar.ars.cantidad > 0) filas.push({ moneda: "ARS", total: porAcreditar.ars });
  if (porAcreditar.usd.cantidad > 0) filas.push({ moneda: "USD", total: porAcreditar.usd });
  return filas;
}
